import React from 'react';
import styled from 'styled-components';
import { COLOR_SYSTEM, Text, Icon, Badge } from 'components/atoms';
import { TestExperiment as Experiment, TestStatus } from 'types/aiReviewTest';

const formatDateTime = (date: string | Date | undefined) => {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface ExperimentResultsTableProps {
  experiments: Experiment[];
  onExperimentClick: (experiment: Experiment) => void;
}

const ExperimentResultsTable: React.FC<ExperimentResultsTableProps> = ({
  experiments,
  onExperimentClick,
}) => {
  const getStatusBadge = (status: TestStatus) => {
    const statusConfig = {
      [TestStatus.PENDING]: { label: '대기중', color: 'Gray' as const },
      [TestStatus.RUNNING]: { label: '실행중', color: 'Primary' as const },
      [TestStatus.COMPLETED]: { label: '완료', color: 'Primary' as const },
      [TestStatus.FAILED]: { label: '실패', color: 'Red' as const },
    };

    const config = statusConfig[status];
    return (
      <Badge 
        content={config.label}
        color={config.color}
      />
    );
  };

  const getDuration = (startedAt?: string | Date, completedAt?: string | Date) => {
    if (!startedAt) return '-';
    
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffHours > 0) {
      return `${diffHours}시간 ${diffMins % 60}분`;
    }
    return `${diffMins}분`;
  };

  const getSuccessRate = (experiment: Experiment) => {
    if (!(experiment as any).results) return '-';
    
    const total = (experiment as any).results.total || 0;
    const success = (experiment as any).results.success || 0;
    
    if (total === 0) return '-';
    
    const rate = Math.round((success / total) * 100);
    return `${rate}% (${success}/${total})`;
  };

  const renderTableHeader = () => (
    <TableHeader>
      <HeaderCell style={{ width: '80px' }}>ID</HeaderCell>
      <HeaderCell>실험명</HeaderCell>
      <HeaderCell style={{ width: '120px' }}>상태</HeaderCell>
      <HeaderCell style={{ width: '100px' }}>구성</HeaderCell>
      <HeaderCell style={{ width: '150px' }}>성공률</HeaderCell>
      <HeaderCell style={{ width: '120px' }}>실행 시간</HeaderCell>
      <HeaderCell style={{ width: '180px' }}>생성일시</HeaderCell>
      <HeaderCell style={{ width: '80px' }}></HeaderCell>
    </TableHeader>
  );

  const renderTableRow = (experiment: Experiment) => (
    <TableRow key={experiment.id} onClick={() => onExperimentClick(experiment)}>
      <TableCell style={{ width: '80px' }}>
        <IDCell>#{experiment.id}</IDCell>
      </TableCell>
      <TableCell>
        <NameCell>
          {experiment.name}
          {experiment.description && (
            <Description>{experiment.description}</Description>
          )}
        </NameCell>
      </TableCell>
      <TableCell style={{ width: '120px' }}>
        {getStatusBadge(experiment.status)}
      </TableCell>
      <TableCell style={{ width: '100px' }}>
        <ConfigCount>{experiment.configuration?.length || 0}개</ConfigCount>
      </TableCell>
      <TableCell style={{ width: '150px' }}>
        <ResultCell>{getSuccessRate(experiment)}</ResultCell>
      </TableCell>
      <TableCell style={{ width: '120px' }}>
        <DurationCell>{getDuration(experiment.startedAt, experiment.completedAt)}</DurationCell>
      </TableCell>
      <TableCell style={{ width: '180px' }}>
        <DateCell>{formatDateTime(experiment.createdAt)}</DateCell>
      </TableCell>
      <TableCell style={{ width: '80px' }}>
        <ActionCell>
          <Icon name="Chevron Right" size={16} />
        </ActionCell>
      </TableCell>
    </TableRow>
  );

  if (experiments.length === 0) {
    return (
      <EmptyContainer>
        <Icon name="Inbox" size={48} color={COLOR_SYSTEM.get("Gray")?.[400]} />
        <EmptyText>실험 결과가 없습니다.</EmptyText>
      </EmptyContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        {renderTableHeader()}
        <tbody>
          {experiments.map(renderTableRow)}
        </tbody>
      </Table>
    </TableContainer>
  );
};

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-bottom: 2px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const HeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const TableCell = styled.td`
  padding: 16px;
  vertical-align: middle;
`;

const IDCell = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  font-family: monospace;
`;

const NameCell = styled.div``;

const Description = styled(Text)`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin-top: 2px;
`;

const ConfigCount = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const ResultCell = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const DurationCell = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const DateCell = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const ActionCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  
  &:hover {
    color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 16px;
`;

const EmptyText = styled(Text)`
  font-size: 16px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

export default ExperimentResultsTable;